import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/layout/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function RegisterPage() {
  const { register } = useAuth();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    password: "",
    confirm_password: "",
  });
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const [busy, setBusy] = useState(false);

  const update = (key) => (event) => setForm({ ...form, [key]: event.target.value });

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (form.password !== form.confirm_password) {
      setError("Passwords do not match");
      return;
    }
    setBusy(true);
    const result = await register(form);
    setBusy(false);
    if (result.ok) setDone(result.message);
    else setError(result.error);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#EEF2F8] p-6 okn-grain">
      <div className="w-full max-w-md okn-rise">
        <Logo size={44} className="mb-6" />
        <p className="mono mb-2 text-[11px] uppercase tracking-[0.3em] text-cyan-600">
          Okyanus Hub
        </p>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Create account</h1>

        {done ? (
          <div
            data-testid="register-success"
            className="mt-8 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-6"
          >
            <CheckCircle2 className="text-emerald-600" />
            <p className="mt-3 text-sm text-emerald-700">{done}</p>
            <Link
              to="/login"
              className="mono mt-5 inline-block text-xs uppercase tracking-wider text-cyan-600 hover:underline"
            >
              ← Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
            {[
              ["full_name", "Full name", "text"],
              ["email", "Company email", "email"],
              ["password", "Password", "password"],
              ["confirm_password", "Confirm password", "password"],
            ].map(([key, label, type]) => (
              <div key={key}>
                <Label className="mono text-[11px] uppercase tracking-wider text-slate-600">
                  {label}
                </Label>
                <Input
                  data-testid={`register-${key.replace("_", "-")}-input`}
                  type={type}
                  value={form[key]}
                  onChange={update(key)}
                  className="mt-2 border-slate-200 bg-white text-slate-900"
                  required
                />
              </div>
            ))}
            {error ? (
              <p
                data-testid="register-error"
                className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700"
              >
                {error}
              </p>
            ) : null}
            <Button
              data-testid="register-submit-button"
              disabled={busy}
              className="h-11 w-full rounded-full bg-cyan-500 text-[#04121a] hover:bg-cyan-400"
            >
              {busy ? <Loader2 className="animate-spin" size={17} /> : "Register"}
            </Button>
            <p className="pt-2 text-center text-sm text-slate-500">
              Already registered?{" "}
              <Link to="/login" className="text-cyan-600 hover:underline">
                Sign in
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
